import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { isEqual } from 'lodash';
import Image from 'mui-image';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { DataAPIContext, GET_ONE, UPDATE } from '../api';
import FixedLinearProgress from '../components/FixedLinearProgress';
import DeleteIconButton from '../components/DeleteIconButton';
import ImageActionButton from '../components/ImageActionButton';
import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext, { toLicenseeId } from '../components/FormLicenseeContext';
import imagesAPI from '../components/images';
import LatLngPicker from '../components/LatLngPicker';
import LicenseeFormField from '../components/LicenseeFormField';
import PageMeta from '../components/PageMeta';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import SystemTagsDropdown from '../components/SystemTagsDropdown';
import { a11yProps } from '../components/TabPanel';
import useLeavePrompt from '../components/useLeavePrompt';
import UsedByPanel from '../components/UsedByPanel';
import { ContextStore } from '../store/contextStore';
import { toCoord } from './utils';

const PREVIEW_W = 368;
const PREVIEW_H = 272;

// What the leave prompt compares against the loaded stop.
const formSnapshot = (f) => ({
    name: f.name,
    label: f.label,
    description: f.description,
    warning: f.warning,
    latitude: toCoord(f.latitude),
    longitude: toCoord(f.longitude),
    tags: f.tags.map((t) => t.id),
    tourOperatorId: toLicenseeId(f.tourOperatorId),
    imageChanged: f.imageFile !== undefined,
    attractions: f.attractions.map((a) => ({ id: a.attraction_id ?? null, distance: a.distance })),
});

const BusStopEdit = ({ onSaved }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { settings, isFranchise, hasPerm } = React.useContext(ContextStore);
    const hasWritePerm = hasPerm('vehicle_fleet', 'write');
    const history = useHistory();
    const { id } = useParams();
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [currentTab, setCurrentTab] = React.useState(0);

    const [name, setName] = React.useState('');
    const [label, setLabel] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [warning, setWarning] = React.useState('');
    const [latitude, setLatitude] = React.useState('');
    const [longitude, setLongitude] = React.useState('');
    const [tags, setTags] = React.useState([]);
    const [imageSrc, setImageSrc] = React.useState(null);
    const [imageFile, setImageFile] = React.useState(undefined);
    const [attractions, setAttractions] = React.useState([]);
    const [tourOperatorId, setTourOperatorId] = React.useState(null);
    const [loadedForm, setLoadedForm] = React.useState(null);

    const hasChanges = loadedForm !== null && !isEqual(
        formSnapshot({ name, label, description, warning, latitude, longitude, tags, tourOperatorId, imageFile, attractions }),
        loadedForm
    );
    const leaveWithoutPrompt = useLeavePrompt(hasChanges && hasWritePerm);

    const onLicenseeChange = (id) => {
        setTourOperatorId(id);
    };

    // X, CANCEL and the backdrop all land here; the leave prompt asks first.
    const close = () => history.push('/bus-tours/bus-stops');

    React.useEffect(() => {
        setLoading(true);
        dataAPI(GET_ONE, 'route_stop', { id })
            .then((res) => {
                const data = res.data || {};
                const loaded = {
                    name: data.name || '',
                    label: data.label || '',
                    description: data.description || '',
                    warning: data.warning || '',
                    latitude: data.latitude ?? '',
                    longitude: data.longitude ?? '',
                    tags: data.tags || [],
                    tourOperatorId: data.tour_operator ?? null,
                    imageFile: undefined,
                    attractions: (data.attractions || []).map((a, idx) => ({
                        attraction_id: a.attraction_id,
                        name: a.name || '',
                        sequence: a.sequence ?? idx + 1,
                        distance: a.distance != null ? String(a.distance) : '',
                    })),
                };
                setName(loaded.name);
                setLabel(loaded.label);
                setDescription(loaded.description);
                setWarning(loaded.warning);
                setLatitude(loaded.latitude);
                setLongitude(loaded.longitude);
                setTags(loaded.tags);
                setTourOperatorId(loaded.tourOperatorId);
                setImageSrc(data.image || null);
                setImageFile(undefined);
                setAttractions(loaded.attractions);
                setLoadedForm(formSnapshot(loaded));
            })
            .catch(() => enqueueSnackbar('Error loading bus stop', { variant: 'error' }))
            .finally(() => setLoading(false));
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const onPickImage = (ev) => {
        const file = ev.target.files?.[0];
        if (file) {
            setImageFile(file);
            setImageSrc(URL.createObjectURL(file));
        }
    };

    const clearImage = () => {
        setImageFile(null);
        setImageSrc(null);
    };

    const addAttractionRow = () => {
        setAttractions((prev) => [...prev, { attraction_id: null, name: '', sequence: prev.length + 1, distance: '' }]);
    };

    const updateAttraction = (index, patch) => {
        setAttractions((prev) => prev.map((a, i) => (i === index ? { ...a, ...patch } : a)));
    };

    const removeAttraction = (index) => {
        setAttractions((prev) => prev.filter((_, i) => i !== index));
    };

    const renderedImageSrc = React.useMemo(() => {
        if (!imageSrc) return null;
        if (typeof imageSrc === 'string') return imageSrc;
        return imagesAPI.url(settings?.images?.base, imageSrc, `cover_${PREVIEW_W}x${PREVIEW_H}`);
    }, [imageSrc, settings]);

    const onSave = () => {
        const body = {
            name,
            label: label || null,
            description: description || null,
            warning: warning || null,
            latitude: latitude !== '' && latitude !== null ? parseFloat(latitude) : null,
            longitude: longitude !== '' && longitude !== null ? parseFloat(longitude) : null,
            tags: tags.map((t) => ({ id: t.id })),
            attractions: attractions
                .filter((a) => a.attraction_id != null)
                .map((a, idx) => ({
                    attraction_id: a.attraction_id,
                    sequence: idx + 1,
                    distance: a.distance !== '' ? parseInt(a.distance, 10) : 0,
                })),
            ...(imageFile === null ? { image_id: null } : {}),
        };
        const hasFile = imageFile instanceof File;
        const params = {
            id,
            data: body,
            ...(hasFile ? { files: { image: imageFile }, upload: true } : {}),
        };

        // Success navigates away and unmounts the form, so only failures reset `saving`.
        setSaving(true);
        dataAPI(UPDATE, 'route_stop', params)
            .then((res) => {
                if (res.statusCode && res.statusCode >= 400) {
                    enqueueSnackbar(res.data?.message || 'Error saving bus stop', { variant: 'error' });
                    setSaving(false);
                } else {
                    enqueueSnackbar('Bus stop saved', { variant: 'success' });
                    if (onSaved) onSaved();
                    leaveWithoutPrompt(close);
                }
            })
            .catch(() => {
                enqueueSnackbar('Error saving bus stop', { variant: 'error' });
                setSaving(false);
            });
    };

    return (
        <FormLicenseeContext.Provider value={toLicenseeId(tourOperatorId)}>
            <PageMeta title={name ? `Edit ${name}` : 'Edit bus stop'} />
            <FixedLinearProgress visibility={saving} />
            <Drawer
                anchor="right"
                open
                onClose={close}
                PaperProps={{ sx: { width: { xs: '100%', sm: 400 }, overflowY: 'auto' } }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box
                        sx={{
                            bgcolor: '#f5f5f5',
                            px: 2,
                            py: 2.5,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>Edit bus stop</Typography>
                        <IconButton onClick={close} aria-label="close">
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} indicatorColor="primary" textColor="primary">
                        <Tab label="General" {...a11yProps(0)} />
                        {id && <Tab label="Used by" {...a11yProps(1)} />}
                    </Tabs>

                    {loading ? (
                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <CircularProgress />
                        </Box>
                    ) : currentTab === 1 && id ? (
                        <Box sx={{ flex: 1, p: 2 }}>
                            <UsedByPanel resource="route_stop" id={id} layout="sidebar" />
                        </Box>
                    ) : (
                        <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <LicenseeFormField value={tourOperatorId} onChange={onLicenseeChange} disabled={!hasWritePerm} />
                            <TextField
                                variant="filled"
                                label="Stop Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                fullWidth
                                size="small"
                                required
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                variant="filled"
                                label="Pin Label"
                                value={label}
                                onChange={(e) => setLabel(e.target.value.slice(0, 3))}
                                fullWidth
                                size="small"
                                inputProps={{ maxLength: 3 }}
                                InputLabelProps={{ shrink: true }}
                            />
                            <LatLngPicker
                                latitude={latitude}
                                longitude={longitude}
                                mapHeight={175}
                                onUpdate={({ latitude: lat, longitude: lng }) => {
                                    setLatitude(lat ?? '');
                                    setLongitude(lng ?? '');
                                }}
                                textFieldProps={{ InputLabelProps: { shrink: true } }}
                            />
                            <TextField
                                variant="filled"
                                label="Description (Optional)"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                fullWidth
                                size="small"
                                multiline
                                minRows={3}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                variant="filled"
                                label="Warning"
                                value={warning}
                                onChange={(e) => setWarning(e.target.value)}
                                fullWidth
                                size="small"
                                multiline
                                minRows={2}
                                InputLabelProps={{ shrink: true }}
                            />

                            <SystemTagsDropdown
                                label="Tags"
                                value={tags}
                                onChange={(ev, value) => setTags(value)}
                            />

                            {renderedImageSrc ? (
                                <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
                                    <Image
                                        src={renderedImageSrc}
                                        cover
                                        height={PREVIEW_H}
                                        width="100%"
                                        style={{ borderRadius: 8, display: 'block' }}
                                    />
                                    <Box sx={{ position: 'absolute', top: 11, right: 11 }}>
                                        <DeleteIconButton title="Delete image" overImage allowed={hasWritePerm} onClick={clearImage} />
                                    </Box>
                                    <ImageActionButton
                                        hasImage
                                        overImage
                                        component="label"
                                        disabled={!hasWritePerm}
                                        sx={{ position: 'absolute', bottom: 12, right: 12 }}
                                    >
                                        <input type="file" hidden accept="image/*" onChange={onPickImage} />
                                    </ImageActionButton>
                                </Box>
                            ) : (
                                <ImageActionButton component="label" disabled={!hasWritePerm} sx={{ alignSelf: 'flex-start' }}>
                                    <input type="file" hidden accept="image/*" onChange={onPickImage} />
                                </ImageActionButton>
                            )}

                            <Divider />

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontSize: 18, fontWeight: 500 }}>
                                    Nearby Attractions
                                </Typography>
                                {attractions.map((a, idx) => (
                                    <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <RemoteAutocomplete
                                                resource="attraction"
                                                urlSuffix={null}
                                                label="Attraction"
                                                variant="filled"
                                                size="small"
                                                value={a.attraction_id != null ? { id: a.attraction_id, name: a.name } : null}
                                                onChange={(ev, value) =>
                                                    updateAttraction(idx, {
                                                        attraction_id: value?.id ?? null,
                                                        name: value?.name || '',
                                                    })
                                                }
                                                getOptionLabel={(opt) => opt?.name || ''}
                                                isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                                                optional
                                            />
                                        </Box>
                                        <TextField
                                            variant="filled"
                                            label="Distance (m)"
                                            type="number"
                                            value={a.distance}
                                            onChange={(e) => updateAttraction(idx, { distance: e.target.value })}
                                            size="small"
                                            sx={{ width: 100 }}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                        <DeleteIconButton title="Remove attraction" allowed={hasWritePerm} onClick={() => removeAttraction(idx)} />
                                    </Box>
                                ))}
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={addAttractionRow}
                                    sx={{ alignSelf: 'flex-start', mt: 1 }}
                                >
                                    ADD ATTRACTION
                                </Button>
                            </Box>
                        </Box>
                    )}

                    <Box
                        sx={{
                            borderTop: '1px solid #d8d8d8',
                            p: 2,
                            display: 'flex',
                            gap: 1,
                            position: 'sticky',
                            bottom: 0,
                            bgcolor: 'background.paper',
                            zIndex: 2,
                        }}
                    >
                        <FormActionButtons
                            mode="edit"
                            onSubmit={onSave}
                            submitDisabled={loading || saving || !name || !hasChanges} // CP-8441: waits for a change
                            onCancel={close}
                            size="medium"
                            sx={{ flex: 1 }}
                        />
                    </Box>
                </Box>
            </Drawer>
        </FormLicenseeContext.Provider>
    );
};

export default BusStopEdit;
